import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { serverMessage } from '../../../lib/handoverView';

/**
 * H-FE-1 — the pieces every handover screen is built from, in the approved
 * prototype's shapes (`docs/design/mockups/prototipo-handover-v2.html`):
 * the orange uppercase section title with its chevron, the lifecycle nodes, the
 * category and state chips, the labelled facts, and the one act dialog.
 */

export const ORANGE_HEAD = 'text-[10px] font-extrabold tracking-[0.07em] text-[#ec5f2d] uppercase';

export const btn = {
  primary: 'bg-orange-500 text-orange-950 border border-orange-500 rounded-lg px-4 py-1.5 font-bold text-[13px] disabled:bg-transparent disabled:text-slate-500 disabled:border-slate-700 disabled:cursor-not-allowed',
  outline: 'bg-transparent text-orange-500 border border-orange-500/55 rounded-lg px-4 py-1.5 font-bold text-[13px] disabled:opacity-50',
  ghost: 'bg-transparent text-slate-300 border border-slate-700 rounded-lg px-4 py-1.5 font-semibold text-[13px] disabled:opacity-50',
  danger: 'bg-transparent text-red-400 border border-red-400/40 rounded-lg px-4 py-1.5 font-bold text-[13px] disabled:opacity-50',
  ok: 'bg-emerald-500 text-emerald-950 border border-emerald-500 rounded-lg px-4 py-1.5 font-bold text-[13px] disabled:opacity-50',
  text: 'bg-transparent border-0 text-orange-500 font-bold text-[12.5px] underline underline-offset-2 p-0 whitespace-nowrap disabled:text-slate-500 disabled:no-underline',
  sm: 'px-2.5 py-1 text-[12px]',
};

export const why = 'text-[12.5px] text-slate-400 bg-[#0b1222] border border-slate-800 rounded-lg px-3 py-2';

const ALERT = {
  info: 'border-sky-300/30 bg-sky-400/10 text-sky-200',
  warn: 'border-amber-400/35 bg-amber-400/10 text-amber-100',
  bad: 'border-red-400/40 bg-red-400/10 text-red-200',
  ok: 'border-emerald-400/35 bg-emerald-400/10 text-emerald-200',
};

export function Alert({ tone = 'info', children, testId, className = '' }) {
  return (
    <div className={`rounded-lg border text-[12.5px] px-3 py-2.5 ${ALERT[tone]} ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/** A section with the orange uppercase title, its count, a chevron and actions on the right. */
export function Section({
  title, count, defaultOpen = true, right = null, children, testId, bordered = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`px-4 py-3.5 ${bordered ? 'border-t border-slate-800' : ''}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h4 className={`m-0 ${ORANGE_HEAD}`}>{title}</h4>
        {count !== undefined ? <span className="text-[11px] text-slate-500" data-testid={testId ? `${testId}-count` : undefined}>{count}</span> : null}
        <button
          type="button"
          aria-label={open ? 'Collapse' : 'Expand'}
          data-testid={testId ? `${testId}-toggle` : undefined}
          onClick={() => setOpen((o) => !o)}
          className="border border-slate-700 rounded-md text-slate-400 px-1.5 py-0.5 hover:text-slate-50 hover:border-orange-500"
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        {right ? <span className="ml-auto flex gap-1.5 flex-wrap">{right}</span> : null}
      </div>
      {open ? children : null}
    </div>
  );
}

/**
 * The lifecycle, as on the asset and the NCR: `steps` = [{lab, who}], `cur` the
 * index of the current step (steps before it are done), `allDone` ticks them
 * all, `off` greys them all (a cancelled record).
 */
export function Lifecycle({
  steps, cur, allDone = false, off = false, note, testId,
}) {
  return (
    <div data-testid={testId}>
      <div className="flex items-start overflow-x-auto pb-1.5 pt-1.5">
        {steps.map((s, i) => {
          const look = off ? 'off' : (allDone || i < cur ? 'done' : (i === cur ? 'cur' : 'off'));
          return (
            <div key={s.lab} className="flex-1 min-w-[110px] text-center px-1" data-testid={testId ? `${testId}-${i}` : undefined} data-look={look}>
              <div className={`w-[22px] h-[22px] rounded-full border-2 mx-auto mb-1.5 flex items-center justify-center text-[11px] ${look === 'done'
                ? 'border-emerald-400 bg-emerald-400/10 text-emerald-400'
                : look === 'cur' ? 'border-orange-500 bg-orange-500' : 'border-slate-700 bg-slate-950 opacity-55'}`}>
                {look === 'done' ? '✓' : ''}
              </div>
              <div className={`text-[12px] font-bold leading-tight ${look === 'cur' ? 'text-orange-300' : look === 'off' ? 'text-slate-500 font-semibold' : 'text-slate-50'}`}>{s.lab}</div>
              {s.who ? <div className={`text-[11px] mt-0.5 ${look === 'off' ? 'text-slate-500' : 'text-slate-400'}`}>{s.who}</div> : null}
            </div>
          );
        })}
      </div>
      {note ? <p className="text-[11px] text-slate-500 mt-0.5 mb-0" data-testid={testId ? `${testId}-note` : undefined}>{note}</p> : null}
    </div>
  );
}

const CAT_LOOK = {
  A: 'border-red-400/40 text-red-400',
  B: 'border-amber-400/35 text-amber-400',
  C: 'border-slate-600 text-slate-300',
};

export function CatChip({ cat, label, testId, small = false }) {
  return (
    <span
      className={`inline-block rounded-full border bg-transparent font-bold whitespace-nowrap ${small ? 'px-2 py-px text-[11px]' : 'px-2.5 py-0.5 text-[11.5px]'} ${CAT_LOOK[cat] || CAT_LOOK.C}`}
      data-testid={testId}
    >
      {label || cat}
    </span>
  );
}

const CHIP = {
  state: 'bg-sky-300/10 border border-sky-300/30 text-sky-300',
  done: 'bg-emerald-400/10 border border-emerald-400/35 text-emerald-400',
  flag: 'bg-amber-400/10 border border-amber-400/35 text-amber-400',
  mine: 'bg-orange-500/15 border border-orange-500/50 text-orange-300',
  plain: 'bg-slate-800 text-slate-300',
  void: 'bg-transparent border border-slate-700 text-slate-500',
};

export function Chip({ tone = 'plain', children, testId }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11.5px] font-bold whitespace-nowrap ${CHIP[tone]}`} data-testid={testId}>
      {children}
    </span>
  );
}

export function Pill({ tone, children, testId }) {
  const look = tone === 'ok' ? 'text-emerald-400 border-emerald-400/35'
    : tone === 'amber' ? 'text-amber-400 border-amber-400/35'
      : tone === 'orange' ? 'text-orange-300 border-orange-500/50' : 'text-slate-300 border-slate-700';
  return <span className={`rounded-md px-2.5 py-1 text-[12px] font-bold border whitespace-nowrap ${look}`} data-testid={testId}>{children}</span>;
}

/** Labelled facts: orange uppercase label, the value clear beneath (never an unlabelled trail). */
export function Facts({ items, testId }) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-x-[18px] gap-y-2.5 m-0" data-testid={testId}>
      {items.filter(Boolean).map((it) => (
        <div key={it.label} data-testid={it.testId}>
          <dt className={ORANGE_HEAD}>{it.label}</dt>
          <dd className="m-0 mt-0.5 text-slate-50 text-[13px] font-semibold break-words">
            {it.value}
            {it.sub ? <span className="block text-slate-500 text-[11px] font-normal">{it.sub}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One boxed fact (the punch item's detail column). */
export function Field({ label, children, testId }) {
  return (
    <dl className="bg-[#0b1222] border border-slate-800 rounded-md px-2.5 py-2 mb-1.5" data-testid={testId}>
      <dt className="text-[10.5px] text-slate-500 mb-0.5 uppercase tracking-wide">{label}</dt>
      <dd className="m-0 text-[12.5px] text-slate-300">{children}</dd>
    </dl>
  );
}

const INPUT = 'w-full bg-[#0b1222] border border-slate-700 rounded-lg px-3 py-2 text-slate-50 text-[13px] [color-scheme:dark]';
const LABEL = `block mb-1.5 ${ORANGE_HEAD}`;

/**
 * THE act dialog, in the prototype's `openModal` shape: title, sub-line,
 * fields, Cancel / the act's button (red when it ends or refuses something).
 *
 * fields: {k, label, type: textarea|text|select|radios|static|date|time|file,
 * req, hint, placeholder, options [{v, l, s}], node (static), inline, accept}.
 * `live(values)` may return {message, tone, block} shown under the fields and
 * disabling the button. A refusal from the door is shown word for word
 * (`detail.message`) and the dialog stays open; `refusal(err)` may read it
 * instead (H-FE-3: the completion's named refusals).
 */
export function HoDialog({
  title, sub, fields = [], okLabel, danger = false, size = '', initial = {}, live, onSubmit, onClose,
  testId = 'ho-dialog', children, okTone, refusal = serverMessage,
}) {
  const [values, setValues] = useState(() => {
    const v = {};
    fields.forEach((f) => {
      const opts = f.options || [];
      const dflt = f.type === 'file' ? null
        : (opts.find((o) => o.on)?.v ?? (f.type === 'select' && opts.length ? opts[0].v : (f.value ?? '')));
      v[f.k] = initial[f.k] ?? dflt;
    });
    return v;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const visible = fields.filter((f) => !f.show || f.show(values));
  const filled = visible.every((f) => !f.req || (f.type === 'file' ? !!values[f.k] : String(values[f.k] || '').trim()));
  const liveState = live ? live(values) : null;
  const blocked = !filled || saving || !!liveState?.block;

  const submit = async () => {
    if (blocked) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(refusal(err));
      setSaving(false);
    }
  };

  const width = size === 'wide' ? 'sm:max-w-[1100px]' : size === 'mid' ? 'sm:max-w-[720px]' : 'sm:max-w-[560px]';
  const okClass = danger ? btn.danger : (okTone === 'ok' ? btn.ok : btn.primary);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className={`bg-slate-900 border-slate-700 p-0 gap-0 max-h-screen sm:max-h-[90vh] overflow-y-auto max-w-full h-full sm:h-auto rounded-none sm:rounded-xl ${width}`}
        data-testid={testId}
      >
        <DialogHeader className="px-4 py-3.5 border-b border-slate-800 text-left">
          <DialogTitle className="text-slate-50 text-base">{title}</DialogTitle>
          {sub ? <p className="text-[12.5px] text-slate-400 mt-1 mb-0">{sub}</p> : null}
        </DialogHeader>
        <div className="px-4 py-3.5">
          {visible.map((f) => {
            const id = `${testId}-${f.k}`;
            if (f.type === 'static' && !f.block) {
              return (
                <div key={f.k} className="flex gap-x-3 gap-y-1 items-baseline flex-wrap text-[13px] text-slate-300 py-0.5 mb-2" data-testid={id}>
                  <span className={`${ORANGE_HEAD} min-w-[150px]`}>{f.label}</span>
                  <span>{f.node}</span>
                </div>
              );
            }
            return (
              <div key={f.k} className={`mb-3.5 ${f.inline ? 'inline-block align-top mr-5' : ''}`}>
                {f.label ? (
                  <label className={LABEL} htmlFor={id}>
                    {f.label}
                    {f.req ? <span className="text-red-400 normal-case tracking-normal"> *</span> : null}
                  </label>
                ) : null}
                {f.hint ? <p className="text-[11.5px] text-slate-500 mb-1.5 mt-0">{f.hint}</p> : null}
                {f.type === 'static' ? <div className="text-[13px] text-slate-300" data-testid={id}>{f.node}</div> : null}
                {f.type === 'textarea' ? (
                  <textarea id={id} data-testid={id} className={`${INPUT} min-h-[70px]`} placeholder={f.placeholder || ''}
                    value={values[f.k]} onChange={(e) => set(f.k, e.target.value)} />
                ) : null}
                {f.type === 'text' || !f.type ? (
                  <input id={id} data-testid={id} className={INPUT} placeholder={f.placeholder || ''}
                    value={values[f.k]} onChange={(e) => set(f.k, e.target.value)} />
                ) : null}
                {f.type === 'date' || f.type === 'time' ? (
                  <input id={id} data-testid={id} type={f.type} className={`${INPUT} ${f.type === 'date' ? 'w-[175px]' : 'w-[135px]'}`}
                    value={values[f.k]} onChange={(e) => set(f.k, e.target.value)} />
                ) : null}
                {f.type === 'select' ? (
                  <select id={id} data-testid={id} className={INPUT} value={values[f.k]} onChange={(e) => set(f.k, e.target.value)}>
                    {(f.options || []).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : null}
                {f.type === 'radios' ? (f.options || []).map((o) => {
                  const on = values[f.k] === o.v;
                  return (
                    <label key={o.v} className={`flex gap-2.5 items-start rounded-lg border px-3 py-2.5 mb-1.5 cursor-pointer ${on
                      ? 'border-orange-500/55 bg-orange-500/5' : 'border-slate-700 bg-[#0b1222]'}`}>
                      <input type="radio" name={id} value={o.v} checked={on} data-testid={`${id}-${o.v}`}
                        className="mt-1 accent-orange-500" onChange={() => set(f.k, o.v)} />
                      <span>
                        <b className="block text-slate-50 text-[13px]">{o.l}</b>
                        {o.s ? <span className="block text-slate-400 text-[11.5px]">{o.s}</span> : null}
                      </span>
                    </label>
                  );
                }) : null}
                {f.type === 'file' ? (
                  <input id={id} data-testid={id} type="file" accept={f.accept || 'image/*'}
                    className="block text-[12.5px] text-slate-300 file:mr-3 file:rounded-md file:border file:border-slate-700 file:bg-[#0b1222] file:px-3 file:py-1 file:text-slate-200"
                    onChange={(e) => set(f.k, (e.target.files && e.target.files[0]) || null)} />
                ) : null}
              </div>
            );
          })}
          {children}
          {liveState?.message ? (
            <Alert tone={liveState.tone || 'info'} testId={`${testId}-live`} className="mt-1">{liveState.message}</Alert>
          ) : null}
          {error ? <Alert tone="bad" testId={`${testId}-error`} className="mt-2">{error}</Alert> : null}
        </div>
        <DialogFooter className="px-4 py-3 border-t border-slate-800 gap-2">
          <button type="button" onClick={onClose} className={btn.ghost} data-testid={`${testId}-cancel`}>Cancel</button>
          <button type="button" data-testid={`${testId}-ok`} disabled={blocked} onClick={submit} className={okClass}>
            {okLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
